import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Chip,
  Collapse,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import _ from 'lodash';

import { useAppSelector } from '../../../../hooks';
import { getAuthState } from '../../../../store/AuthSlice';
import {
  getChatState,
  IConversation,
  IConversationUser,
  selectConversation,
} from '../../../../store/V2/ChatSlice';
import BasicLoader from '../../../../components/Loaders/BasicLoader';
import { TP_COLORS } from '../../../../constants';
import { getConversationAvatar, getConversationName } from '../chatHelpers';

interface ConversationsListProps {
  searchText: string;
}

const ConversationsList: React.FC<ConversationsListProps> = ({
  searchText,
}) => {
  const dispatch = useDispatch();

  // Store
  const { user } = useAppSelector(getAuthState);
  const { isLoading, conversations, conversationTypingData } = useAppSelector(
    getChatState
  );

  const handleConversationSelect = (conversation: IConversation) => {
    dispatch(selectConversation(conversation));
  };

  const filteredConversations = _.orderBy(
    conversations.filter((conversation) => {
      const users = conversation.users as IConversationUser[];

      if (users.length === 0) return true;

      const otherUsers = users.filter((u) => u._id !== user?._id);

      return otherUsers.some((u) =>
        `${u.firstname} ${u.lastname}`
          .toLowerCase()
          .includes(searchText.toLowerCase())
      );
    }),
    ['lastMessageTime', 'unseenCount'],
    ['desc', 'desc']
  );

  if (isLoading && conversations.length === 0)
    return (
      <BasicLoader
        loaderProps={{ size: 30 }}
        containerProps={{ height: '65%', alignItems: 'center' }}
      />
    );

  return (
    <List sx={{ overflowY: 'auto' }}>
      {filteredConversations.map((conversation) => {
        const _matchingTypingData = conversationTypingData.find(
          (item) =>
            item.conversationId === conversation._id &&
            item.userId !== user?._id
        );

        return (
          <ListItemButton
            key={conversation._id}
            onClick={() => handleConversationSelect(conversation)}
          >
            <ListItemAvatar
              sx={{
                m: 0,
                display: 'flex',
                justifyContent: 'center',
                width: '80px',
              }}
            >
              {getConversationAvatar(conversation, user?._id)}
            </ListItemAvatar>
            <ListItemText
              sx={{ pl: '5px' }}
              primary={getConversationName(conversation, user?._id)}
              secondary={
                <Collapse in={Boolean(_matchingTypingData)}>
                  <Typography variant="caption">Typing...</Typography>
                </Collapse>
              }
            />
            {conversation.unSeenCount && conversation.unSeenCount ? (
              <Chip
                size="small"
                label={conversation.unSeenCount}
                sx={{ backgroundColor: '#dd2334', color: TP_COLORS.white }}
              />
            ) : null}
          </ListItemButton>
        );
      })}
    </List>
  );
};

export default ConversationsList;
